const UNKNOWN_AGENT_NAMES = new Set([
  '',
  'unknown',
  'unknown agent',
  'n/a',
  'none',
  'null',
  'undefined',
]);

const valueAt = (record, path) => {
  if (!record || typeof record !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(record, path)) return record[path];

  let current = record;
  const parts = path.split('.');
  for (let index = 0; index < parts.length; index += 1) {
    if (!current || typeof current !== 'object') return undefined;
    const remainder = parts.slice(index).join('.');
    if (Object.prototype.hasOwnProperty.call(current, remainder)) return current[remainder];
    current = current[parts[index]];
  }
  return current;
};

const clean = (value) => {
  if (value === undefined || value === null) return undefined;
  const text = String(value).trim();
  return UNKNOWN_AGENT_NAMES.has(text.toLowerCase()) ? undefined : text;
};

const first = (record, paths, { nonNumeric = false } = {}) => {
  for (const path of paths) {
    const value = clean(valueAt(record, path));
    if (value && (!nonNumeric || !/^\d+$/.test(value))) return value;
  }
  return undefined;
};

/**
 * Return the stable name used to identify a custom agent across product pages.
 *
 * Configured telemetry identity wins over enrichment and registry display text.
 * Generic `name`/`label` and traceable span-name fields are deliberately excluded:
 * those describe an operation, not the agent that emitted it.
 */
export const resolveAgentName = (record = {}, fallback = 'Unknown Agent') => {
  const configured = first(record, [
    'attributes.agent_id',
    'attributes.service.name',
    'resource.service.name',
    'resource.attributes.service.name',
    'configured_agent_id',
    'service_name',
  ]);
  if (configured) return configured;

  const stableIdentifier = first(record, [
    'agent_identifier',
    'enrichment.agent_identifier',
    'agent_id',
    'enrichment.agent_id',
  ], { nonNumeric: true });
  if (stableIdentifier) return stableIdentifier;

  const exactDisplayFallback = first(record, [
    'agent_name',
    'enrichment.agent_name',
    'agent_display_name',
  ]);
  if (exactDisplayFallback) return exactDisplayFallback;

  return first(record, ['agent_id', 'enrichment.agent_id']) || fallback;
};

export const withCanonicalAgentName = (record = {}, fallback = 'Unknown Agent') => ({
  ...record,
  agent_name: resolveAgentName(record, fallback),
});

